import {
	ColumnDef,
	flexRender,
	getCoreRowModel,
	useReactTable,
} from '@tanstack/react-table';

import PaginationBottomTable from './PaginationBottomTable';
import { IPaginationTable } from './interfaces/IPaginationTable';

import { ICollection } from '@/interfaces/api/collection/ICollection';

type TableProps<T extends object> = {
	collection: ICollection<T>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	columns: ColumnDef<T, any>[];
	paginationSection?: IPaginationTable;
	tableStyles?: ITableStyles;
	handleSelectRow?: (item: T) => void;
	dataTest?: string;
};

interface ITableStyles {
	tableBox?: string;
	table?: string;
	tableHead?: string;
	tableBody?: string;
	tableBodyTr?: string;
	tableBodyTd?: string;
}

const Table = <T extends object>({
	collection,
	columns,
	paginationSection,
	tableStyles,
	handleSelectRow,
	dataTest,
}: TableProps<T>) => {
	const {
		tableBox,
		table: tableStyle,
		tableHead,
		tableBody,
		tableBodyTd,
		tableBodyTr,
	} = tableStyles || {};
	const DEFAULT_PAGE_SIZE = 10;

	const table = useReactTable({
		data: collection.data,
		columns,
		initialState: {
			pagination: {
				pageSize: paginationSection
					? paginationSection.pageSize
					: DEFAULT_PAGE_SIZE,
			},
		},
		getCoreRowModel: getCoreRowModel(),
	});

	const handleUrl = (item: T) => {
		if (handleSelectRow) {
			handleSelectRow(item);
		}
	};

	return (
		<div className={'w-full h-fit ' + tableBox}>
			<div className="w-full overflow-x-auto">
				<table
					className={'flex flex-col w-full min-w-max ' + tableStyle}
					data-test={dataTest}
				>
					<thead
						className={
							tableHead +
							' bg-purpleBrand-200 text-black font-semibold text-md px-4 py-2 rounded-xl w-full'
						}
					>
						{table.getHeaderGroups().map((headerGroup) => (
							<tr
								key={headerGroup.id}
								className="uppercase flex justify-between w-full items-center gap-2"
							>
								{headerGroup.headers.map((header) => (
									<th key={header.id} className="py-4 font-medium text-left">
										{header.isPlaceholder ? null : (
											<div>
												{flexRender(
													header.column.columnDef.header,
													header.getContext(),
												)}
											</div>
										)}
									</th>
								))}
							</tr>
						))}
					</thead>
					<tbody className={tableBody}>
						{table.getRowModel().rows.map((row, idexRow) => {
							return (
								<tr
									onClick={
										handleSelectRow ? () => handleUrl(row.original) : undefined
									}
									key={row.id}
									className={
										'flex justify-between items-center hover:bg-stone-300 h-fit min-h-[40px] gap-2 bg-white border rounded-xl shadow-md py-4 px-4 my-2 ' +
										tableBodyTr
									}
									data-test={`row-${idexRow}`}
								>
									{row.getVisibleCells().map((cell, idexCell) => (
										<td
											key={cell.id}
											className={`flex items-center ${tableBodyTd}`}
											data-test={`cell-${idexCell}`}
										>
											{flexRender(
												cell.column.columnDef.cell,
												cell.getContext(),
											)}
										</td>
									))}
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>

			{paginationSection ? (
				<PaginationBottomTable {...paginationSection} />
			) : (
				<div className="flex sm:flex-row flex-col w-full items-center gap-2 text-xs px-4 py-2 border-t-[1px]"></div>
			)}
		</div>
	);
};

export default Table;
